import React, { useEffect, useState, useMemo } from "react";
import { Button, notification, message } from "antd";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserTableColumns } from ".//UserInterfaces";
import jwtDecode from "jwt-decode";
import { CirclePlus } from "lucide-react";
import {
  fetchUsers,
  updateUserStatus,
  deleteUser,
} from "../../services/UserManagementAPIs";
import { UserManagementProps, User } from "./UserInterfaces";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledInput from "src/components/reusable/StyledInput";
import ConfirmationModal from "src/components/reusable/ConfirmationModal";
import CampaignTable from "../../components/reusable/CampaignTable/CampaignTable";
import { TablePagePagination } from "src/components/common";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import { CustomIcon } from "src/components/reusable/CustomIcon";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";

const USERS_PAGE_SIZE = 6;

const UserManagement: React.FC<UserManagementProps> = ({ role_id }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasFetchedUsers = React.useRef(false);
  const token = params.get("token");
useEffect(() => {
  if (!token) {return;}
  try {
    const decodedToken = jwtDecode<any>(token);
    localStorage.setItem("user_id", decodedToken.user_id || "");
    localStorage.setItem("tenant_id", decodedToken.tenant_id || "");
    localStorage.setItem("role_id", decodedToken.role_id || "");
    localStorage.setItem("api_key", decodedToken.api_key || "");
    const newUrl = `${globalThis.location.origin}${globalThis.location.pathname}`;
    globalThis.history.replaceState({}, document.title, newUrl);
  } catch {}
}, [token]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const api_key = localStorage.getItem("api_key") || "";
  const user_id = localStorage.getItem("user_id") || "";
  const tenant_id = localStorage.getItem("tenant_id") || "";
  const [users, setUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isSocialListeningEnabled, setIsSocialListeningEnabled] = useState(true);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isUserLimitReached =
    users.filter(u => u.accessLevel !== "Admin").length >= 60;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const storedApiKey = localStorage.getItem("api_key");
    if (!storedApiKey) {
      navigate("/log-in");
    }
  }, [navigate]);

  useEffect(() => {
    if (hasFetchedUsers.current) {return;}
    hasFetchedUsers.current = true;

    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const { users: fetchedUsers, socialListeningEnabled } = await fetchUsers(
          api_key,
          tenant_id
        );
        setUsers(fetchedUsers);
        setIsSocialListeningEnabled(socialListeningEnabled);
      } catch (error) {
        const err = error as any;
        message.error(err.message || t("userManagement.messages.failedFetchUsers"));
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [api_key, tenant_id]);

  const toggleStatus = async (id: number) => {
    const user = users.find(u => u.id === id);
    if (user) {
      const updatedIsActive = !user.isActive;
      setUsers(prev =>
        prev.map(u => (u.id === id ? { ...u, isActive: updatedIsActive } : u))
      );
      try {
        await updateUserStatus(
          api_key,
          id,
          updatedIsActive,
          user.accessLevel,
          tenant_id
        );
      } catch (error) {
        const err = error as any;
        message.error(err.message || t("userManagement.messages.failedUpdateUserStatus"));
      }
    }
  };
  const rowSelection = useMemo(() => ({
  selectedRowKeys,
  onChange: (selectedKeys: React.Key[]) => setSelectedRowKeys(selectedKeys),
  getCheckboxProps: (record: User) => ({
    disabled: record.accessLevel === "Admin",
  }),
}), [selectedRowKeys]);

const handleBulkDelete = async () => {
  try {
    const deletableIds = selectedRowKeys.filter(id => {
  const user = users.find(u => u.id === id);
  return user && user.accessLevel !== "Admin";
});

    await Promise.all(
      deletableIds.map(id => deleteUser(api_key, Number(id), tenant_id))
    );

    message.success(t("userManagement.messages.selectedUsersDeleted"));

    setUsers(users.filter(user => !deletableIds.includes(user.id)));
    setSelectedRowKeys([]);
  } catch (error) {
    const err = error as any;
    message.error(err?.message || t("userManagement.messages.failedDeleteSelectedUsers"));
  } finally {
    setIsModalOpen(false);
  }
};


  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) {return;}
    setDeletingUserId(userToDelete.id);
    try {
      await deleteUser(api_key, userToDelete.id, tenant_id);
      message.success(t("userManagement.messages.userDeletedSuccessfully"));
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
    } catch (error) {
      const err = error as any;
      message.error(err?.message || t("userManagement.messages.failedDeleteUser"));
    } finally {
      setDeletingUserId(null);
      setUserToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  const cancelDeleteModal = () => {
    setUserToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleCancelModal = () => {
    setIsModalOpen(false);
  };

  const handleAccessLevelChange = async (
    userId: number,
    accessLevel: string
  ) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUsers(prev =>
        prev.map(u => (u.id === userId ? { ...u, accessLevel } : u))
      );
      try {
        await updateUserStatus(
          api_key,
          userId,
          user.isActive,
          accessLevel,
          tenant_id
        );
      } catch (error) {
        const err = error as any;
        message.error(err.message || t("userManagement.messages.failedUpdateAccessLevel"));
      }
    }
  };

  const handleAddUser = () => {
    if (isUserLimitReached) {
      notification.error({
        message: t("userManagement.messages.userLimitExceededTitle"),
        description: t("userManagement.messages.userLimitExceededDescription"),
      });
      return;
    }
    setIsAddUserModalOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleUpdateUser = (updatedUser: User) => {
    setUsers(prev =>
      prev.map(u => (u.id === updatedUser.id ? updatedUser : u))
    );
    setIsEditUserModalOpen(false);
    setSelectedUser(null);
  };

  const filteredUsers = users.filter(user => {
    const matchSearch =
      user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchText.toLowerCase());
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.isActive) ||
      (statusFilter === "inactive" && !user.isActive);
    const matchRole =
      roleFilter.length === 0 || roleFilter.includes(user.accessLevel);
    return matchSearch && matchStatus && matchRole;
  });
  const localizedFilteredUsersCount = useMemo(
    () => formatNumber(filteredUsers.length, {}, i18n.language),
    [filteredUsers.length, i18n.language]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE));
  const displayedPage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (displayedPage - 1) * USERS_PAGE_SIZE,
    displayedPage * USERS_PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter, roleFilter]);

  return (
    <div className="user-management-container">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="user-management-product-header">
            <p className="User-dashboard-header">{t("userManagement.title")}</p>
            <p className="user-management-description">
              {t("userManagement.description")}
            </p>
          </div>

          <div className="user-management-box">
            <div className="user-management-card-header">
              <div className="user-management-card-title">
                {t("userManagement.listHeader", { count: localizedFilteredUsersCount })}
              </div>

              <div className="user-management-card-actions">
                <span className="user-management-delete-icon-wrap">
                  <CustomIcon
                    name="delete"
                    tooltip={t("userManagement.deleteUserButton")}
                    onClick={
                      selectedRowKeys.length === 0
                        ? undefined
                        : () => setIsModalOpen(true)
                    }
                    size={18}
                    color={selectedRowKeys.length === 0 ? "#141B34" : "#2c68ad"}
                    className="user-management-delete-icon"
                    style={
                      selectedRowKeys.length === 0
                        ? { opacity: 0.45, cursor: "not-allowed" }
                        : { opacity: 1, cursor: "pointer" }
                    }
                  />
                </span>

                <StyledTooltip
                  content={isUserLimitReached ? t("userManagement.maxUserLimitReached") : ""}
                  placement="top"
                >
                  <span>
                    <Button
                      type="primary"
                      className="button button--primary user-management-button"
                      onClick={handleAddUser}
                      disabled={isUserLimitReached}
                    >
                      <CirclePlus size={16}/>
                      {t("userManagement.addUserButton")}
                    </Button>
                  </span>
                </StyledTooltip>
              </div>
            </div>

            <div className="user-management-filters">
              <StyledInput
                className="user-input"
                placeholder={t("userManagement.searchPlaceholder")}
                value={searchText}
                onChange={handleSearchChange}
              />

              <div className="user-management-filter-group">
                <StyledDropdown
                  value={statusFilter}
                  className="usermanagement-dropdown user-management-filter-dropdown-role"
                  onChange={value =>
                    setStatusFilter(value as "all" | "active" | "inactive")
                  }
                  popupClassName="custom-dropdown-menu"
                  options={[
                    { label: t("userManagement.filters.allUsers"), value: "all" },
                    { label: t("userManagement.filters.activeUsers"), value: "active" },
                    { label: t("userManagement.filters.inactiveUsers"), value: "inactive" },
                  ]}
                />

                <StyledDropdown
                  mode="multiple"
                  showSearch={false}
                  placeholder={t("userManagement.filters.selectRoles")}
                  className="usermanagement-dropdown user-management-filter-dropdown-role"
                  value={roleFilter}
                  onChange={value => setRoleFilter(value as string[])}
                  popupClassName="custom-dropdown-menu"
                  options={[
                    { label: t("userManagement.role.viewer"), value: "viewer" },
                    { label: t("userManagement.role.admin"), value: "Admin" },
                  ]}
                />
              </div>
            </div>

            <CampaignTable
              rowKey="id"
              rowSelection={rowSelection}
              rowClassName={(record: User) =>
                record.accessLevel === "Admin" || record.last_login === null
                  ? "user-row-disabled"
                  : ""
              }
              columns={getUserTableColumns(
                handleAccessLevelChange,
                toggleStatus,
                handleEditUser,
                handleDeleteClick,
                deletingUserId,
                t,
                isArabic
              )}
              data={paginatedUsers}
              className="user-management-table"
              customPagination={
                <TablePagePagination
                  currentPage={displayedPage}
                  totalPages={totalPages}
                  onPrevious={() => setCurrentPage(page => Math.max(1, page - 1))}
                  onNext={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                />
              }
            />
          </div>

          <ConfirmationModal
            modalOpen={isModalOpen}
            onCancel={handleCancelModal}
            onConfirm={handleBulkDelete}
            title={t("userManagement.deleteSelectedTitle")}
            description={t("userManagement.deleteSelectedDescription")}
            icon="/images/delete.png"
            confirmText={t("userManagement.confirmDelete")}
            cancelText={t("userManagement.cancel")}
          />

<ConfirmationModal
  modalOpen={isDeleteModalOpen}
  onCancel={cancelDeleteModal}
  onConfirm={confirmDeleteUser}
  title={t("userManagement.deleteSingleTitle")}
  description={t("userManagement.deleteSingleDescription", { name: userToDelete?.name || "" })}
  icon="/images/delete.png"
  confirmText={t("userManagement.confirmDelete")}
  cancelText={t("userManagement.cancel")}
  confirmButtonProps={{ loading: !!deletingUserId }}
/>


          <AddUserModal
            isOpen={isAddUserModalOpen}
            onClose={() => setIsAddUserModalOpen(false)}
            addUser={(newUser: User) => setUsers([...users, newUser])}
            tenant_id={tenant_id}
            user_id={user_id}
            api_key={api_key}
            isSocialListeningEnabled={isSocialListeningEnabled}
          />

          <EditUserModal
            isOpen={isEditUserModalOpen}
            onClose={() => setIsEditUserModalOpen(false)}
            user={selectedUser}
            onUpdateUser={handleUpdateUser}
            isSocialListeningEnabled={isSocialListeningEnabled}
          />
        </>
      )}
    </div>
  );
};

export default UserManagement;
